import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Star, CheckCircle, XCircle, Trash2, Search, MessageSquare, Clock } from 'lucide-react';
import { Input } from '@/components/ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ReviewsManagement = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, pending, approved, rejected
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchReviews();
  }, []);

  const fetchReviews = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/reviews/all`);
      setReviews(response.data);
    } catch (error) {
      toast.error('Failed to fetch reviews');
    } finally {
      setLoading(false);
    }
  };

  const handleModerate = async (reviewId, status) => {
    try {
      await axios.put(`${API}/reviews/${reviewId}/moderate`, { status });
      toast.success(`Review ${status}`);
      fetchReviews();
    } catch (error) {
      toast.error('Failed to moderate review');
    }
  };

  const handleDelete = async (reviewId) => {
    if (!window.confirm('Delete this review permanently?')) return;
    try {
      await axios.delete(`${API}/reviews/${reviewId}`);
      toast.success('Review deleted');
      fetchReviews();
    } catch (error) {
      toast.error('Failed to delete review');
    }
  };

  const filteredReviews = reviews.filter(r => {
    const matchesFilter = filter === 'all' || r.status === filter;
    const matchesSearch = r.user_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          r.comment?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const pendingCount = reviews.filter(r => r.status === 'pending').length;
  const approvedCount = reviews.filter(r => r.status === 'approved').length;
  const rejectedCount = reviews.filter(r => r.status === 'rejected').length;

  const renderStars = (rating) => (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map(s => (
        <Star key={s} className={`h-4 w-4 ${s <= rating ? 'fill-amber-400 text-amber-400' : 'text-slate-200'}`} />
      ))}
    </div>
  );

  const statusColors = {
    pending: 'bg-yellow-100 text-yellow-700',
    approved: 'bg-green-100 text-green-700',
    rejected: 'bg-red-100 text-red-700'
  };

  return (
    <div data-testid="reviews-management-page">
      <div className="mb-6">
        <h2 className="text-2xl font-heading font-bold uppercase flex items-center gap-2">
          <MessageSquare className="h-6 w-6 text-primary" />
          Reviews & Ratings
        </h2>
        <p className="text-slate-500 text-sm mt-1">Moderate customer reviews and ratings</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        <Card className="p-4 border-slate-200 cursor-pointer hover:shadow-md transition-shadow" onClick={() => setFilter('all')}>
          <p className="text-xs font-medium text-slate-500 uppercase">Total</p>
          <p className="text-2xl font-bold">{reviews.length}</p>
        </Card>
        <Card className={`p-4 border-slate-200 cursor-pointer hover:shadow-md transition-shadow ${filter === 'pending' ? 'ring-2 ring-yellow-400' : ''}`} onClick={() => setFilter('pending')}>
          <p className="text-xs font-medium text-yellow-600 uppercase">Pending</p>
          <p className="text-2xl font-bold text-yellow-700">{pendingCount}</p>
        </Card>
        <Card className={`p-4 border-slate-200 cursor-pointer hover:shadow-md transition-shadow ${filter === 'approved' ? 'ring-2 ring-green-400' : ''}`} onClick={() => setFilter('approved')}>
          <p className="text-xs font-medium text-green-600 uppercase">Approved</p>
          <p className="text-2xl font-bold text-green-700">{approvedCount}</p>
        </Card>
        <Card className={`p-4 border-slate-200 cursor-pointer hover:shadow-md transition-shadow ${filter === 'rejected' ? 'ring-2 ring-red-400' : ''}`} onClick={() => setFilter('rejected')}>
          <p className="text-xs font-medium text-red-600 uppercase">Rejected</p>
          <p className="text-2xl font-bold text-red-700">{rejectedCount}</p>
        </Card>
      </div>

      <div className="mb-6 relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 h-4 w-4" />
        <Input placeholder="Search reviews..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" />
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : filteredReviews.length === 0 ? (
        <Card className="p-12 text-center border-slate-200">
          <MessageSquare className="h-16 w-16 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-700">No reviews found</h3>
          <p className="text-slate-500">Reviews will appear here when customers submit them.</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {filteredReviews.map(review => (
            <Card key={review.id} className="p-5 border-slate-200 hover:shadow-md transition-all">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary/20 to-primary/30 flex items-center justify-center text-primary font-bold text-sm">
                    {review.user_name?.charAt(0)?.toUpperCase() || '?'}
                  </div>
                  <div>
                    <p className="font-semibold">{review.user_name}</p>
                    <div className="flex items-center gap-2">
                      {renderStars(review.rating)}
                      <span className="text-xs text-slate-400 flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {new Date(review.created_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                </div>
                <Badge className={statusColors[review.status] || 'bg-slate-100 text-slate-500'}>
                  {review.status}
                </Badge>
              </div>

              <p className="text-slate-700 mb-4 pl-[52px]">{review.comment}</p>

              <div className="flex items-center justify-between pl-[52px]">
                <p className="text-xs text-slate-400">Product ID: {review.product_id?.substring(0, 8)}...</p>
                <div className="flex gap-2">
                  {review.status !== 'approved' && (
                    <Button size="sm" variant="outline" className="text-green-600 hover:bg-green-50" onClick={() => handleModerate(review.id, 'approved')}>
                      <CheckCircle className="h-4 w-4 mr-1" /> Approve
                    </Button>
                  )}
                  {review.status !== 'rejected' && (
                    <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50" onClick={() => handleModerate(review.id, 'rejected')}>
                      <XCircle className="h-4 w-4 mr-1" /> Reject
                    </Button>
                  )}
                  <Button size="sm" variant="outline" className="text-slate-500 hover:bg-slate-50" onClick={() => handleDelete(review.id)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default ReviewsManagement;
